import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isValid from 'date-fns/isValid';
import sub from 'date-fns/sub';
import startOfToday from 'date-fns/startOfToday';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import startOfYear from 'date-fns/startOfYear';
import endOfWeek from 'date-fns/endOfWeek';
import endOfMonth from 'date-fns/endOfMonth';
import endOfYear from 'date-fns/endOfYear';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import differenceInMonths from 'date-fns/differenceInMonths';
import { add, differenceInDays } from 'date-fns';
import { localeMap } from './datefns-locale';
import { YESTERDAY } from './dates-const';

export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_FORMAT_2 = 'd MMMM yyyy';
export const DATE_FORMAT_WITH_TIME = 'yyyy-MM-dd HH:mm:ss';
export const TIME_FORMAT = 'HH:mm';

function formatTimestamp(timestamp, formatStyle, options) {
  const parsedTimestamp = parseISO(timestamp);
  return format(isValid(parsedTimestamp) ? parsedTimestamp : new Date(timestamp), formatStyle, options);
}

export function formatTimestampAsTime(timestamp) {
  return timestamp ? formatTimestamp(timestamp, TIME_FORMAT) : '';
}

export function formatTimestampAsDate(timestamp, format, options) {
  return timestamp ? formatTimestamp(timestamp, format ? format : DATE_FORMAT, options) : '';
}

export function formatTimestampAsDateWithTime(timestamp) {
  return timestamp ? formatTimestamp(timestamp, DATE_FORMAT_WITH_TIME) : '';
}

export function getDateAtStartOfToday() {
  return startOfToday();
}

export function getDateAtEndOfToday() {
  return endOfDay(new Date());
}

export function getDateAtStartOfGivenDate(date) {
  return startOfDay(date);
}

export function getDateAtEndOfGivenDate(date) {
  return endOfDay(date);
}

export function getDateAfterDeductFromToday(numberOfDeductedYears, numberOfDeductedMonths, numberOfDeductedWeeks, numberOfDeductedDays) {
  return sub(new Date(),
    {
      years: numberOfDeductedYears,
      months: numberOfDeductedMonths,
      weeks: numberOfDeductedWeeks,
      days: numberOfDeductedDays,
    },
  );
}

export function getTodayDate() {
  return new Date();
}

export function getDayBeforeYesterdayDate() {
  return new Date(new Date().setDate(new Date().getDate() - 2));
}

export function getYesterdayDate() {
  return new Date(new Date().setDate(new Date().getDate() - 1));
}

export function getDateTodayAtGivenHour(hour) {
  return new Date().setHours(hour, 0, 0);
}

export function getDateAtStartOfWeek(date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

export function getDateAtEndOfWeek(date) {
  return endOfWeek(date, { weekStartsOn: 1 });
}

export function getDateAtStartOfMonth(date) {
  return startOfMonth(date);
}

export function getDateAtEndOfMonth(date) {
  return endOfMonth(date);
}

export function getDateAtStartOfYear(date) {
  return startOfYear(date);
}

export function getDateAtEndOfYear(date) {
  return endOfYear(date);
}

export function getDifferenceInDaysBetweenTwoDates(dateFrom, dateTo) {
  
  return differenceInDays(getDateAtStartOfGivenDate(new Date(dateTo)), getDateAtEndOfGivenDate(new Date(dateFrom)));
}

export function getDifferenceInMonthBetweenTwoDates(dateFrom, dateTo) {
  return differenceInMonths(getDateAtStartOfGivenDate(dateTo), getDateAtEndOfGivenDate(dateFrom));
}

export const yesterdayDate = new Date(new Date().setDate(new Date().getDate() - 1));

export const todayDate = new Date();

export function getSelectedYear(selectedMonth, currentDate) {
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  if (selectedMonth < currentMonth) {
    return currentYear;
  }
  
  if (selectedMonth > currentMonth) {
    return currentYear - 1;
  }
  
  return currentMonth === 12 ? currentYear - 1 : currentYear;
}

export function upUntilCurrentMonthOnly(array) {
  const currentMonth = new Date().getMonth();
  return array.filter(month => month.value <= currentMonth);
}

export const getDateRangeString = (data, languageState) => {
  if (data.length === 0) {
    return false;
  }
  
  const firstDay = new Date(data[0]?.Datum);
  const lastDay = new Date(data[data.length - 1]?.Datum);
  const dateFormat = DATE_FORMAT_2;
  const locale = localeMap[languageState];
  
  const formattedFirstDay = formatTimestampAsDate(firstDay, dateFormat, { locale });
  const formattedLastDay = formatTimestampAsDate(lastDay, dateFormat, { locale });
  return `${formattedFirstDay} - ${formattedLastDay}`;
};

export const formatStringAsTime = (hour, minute) => {
  const formattedHour = hour.toString().padStart(2, '0');
  const formattedMinute = minute.toString().padStart(2, '0');
  
  return `${formattedHour}:${formattedMinute}`;
};

export const applyBusinessStartTimeAsDefault = (fields, salesStartTime) => {
  return fields.map(field => {
    if (field.name === 'dateTimeFrom' && salesStartTime?.formattedSalesStartTime) {
      const combinedTime = `${formatTimestampAsDate(field.initialValue)} ${salesStartTime.formattedSalesStartTime}`;
      field.initialValue = parseISO(combinedTime);
    }
    return field;
  });
};

export const dateToNotExceedYesterday = (dateTo) => {
  const dateWithTime = new Date(dateTo).setHours(0, 0, 0, 0);
  
  if (dateWithTime > new Date(YESTERDAY.option.dateFrom)) {
    return formatTimestampAsDate(new Date(YESTERDAY.option.dateFrom));
  }
  return dateTo;
};

export function convertToSimpleDate(verboseDateString) {
  const date = new Date(verboseDateString);
  return format(date, 'yyyy-MM-dd');
}

export function getFormattedPreviousDay(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const date = parseISO(normalizedDate);
  const previousDay = sub(date, { days: 1 });
  return { start: previousDay, end: previousDay };
}

export function getFormattedNextDay(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const date = parseISO(normalizedDate);
  const nextDay = add(date, { days: 1 });
  return { start: nextDay, end: nextDay };
}

export function getFormattedPreviousWeek(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const startOfCurrentWeek = getDateAtStartOfWeek(new Date(normalizedDate));
  const previousWeekStart = sub(startOfCurrentWeek, { weeks: 1 });
  const previousWeekEnd = getDateAtEndOfWeek(previousWeekStart);
  return {
    start: previousWeekStart,
    end: previousWeekEnd,
  };
}

export function getFormattedNextWeek(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const startOfCurrentWeek = getDateAtStartOfWeek(new Date(normalizedDate));
  const nextWeekStart = add(startOfCurrentWeek, { weeks: 1 });
  const nextWeekEnd = getDateAtEndOfWeek(nextWeekStart);
  
  return {
    start: nextWeekStart,
    end: nextWeekEnd,
  };
}

export function getFormattedLastTwoWeeks(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const startOfCurrentWeek = getDateAtStartOfWeek(new Date(normalizedDate));
  const twoWeeksAgoStart = sub(startOfCurrentWeek, { weeks: 2 });
  const lastWeekEnd = sub(startOfCurrentWeek, { days: 1 });
  return {
    start: twoWeeksAgoStart,
    end: lastWeekEnd,
  };
}

export function getFormattedNextTwoWeeks(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const startOfCurrentWeek = getDateAtStartOfWeek(new Date(normalizedDate));
  const nextTwoWeeksStart = add(startOfCurrentWeek, { weeks: 1 });
  const twoWeeksFromNowEnd = add(nextTwoWeeksStart, { weeks: 2, days: -1 });
  
  return {
    start: nextTwoWeeksStart,
    end: twoWeeksFromNowEnd,
  };
}

export function getFormattedPreviousMonth(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const date = parseISO(normalizedDate);
  const startOfLastMonth = startOfMonth(sub(date, { months: 1 }));
  const endOfLastMonth = endOfMonth(startOfLastMonth);
  
  return {
    start: startOfLastMonth,
    end: endOfLastMonth,
  };
}

export function getFormattedNextMonth(dateString) {
  const normalizedDate = convertToSimpleDate(dateString);
  const date = parseISO(normalizedDate);
  const startOfNextMonth = startOfMonth(add(date, { months: 1 }));
  const endOfNextMonth = endOfMonth(startOfNextMonth);
  
  return {
    start: startOfNextMonth,
    end: endOfNextMonth,
  };
}